import React, { useState, useEffect } from 'react';
import { generateHTML } from '../utils/exportHTML';
import { API } from '../services/api';
import { useOrganization } from '../contexts/OrganizationContext';
import SaveDropdown from './SaveDropdown';
import './DashboardToolbar.css';

function DashboardToolbar({
    apiStatus,
    apiEnabled,
    onSave,
    onClear,
    grid,
    signatureName,
    containerWidth,
    previewMode,
    setPreviewMode,
    onRename,
    onInsertTemplate,
    isTemplateMode = false,
    onSchedule,
    onViewScheduledChanges,
    hasEntity = false,
    entityId = null,
    onRefreshNeeded,
    directoryMembers = [],
    previewMember,
    setPreviewMember
}) {
    const { subscriptionPlan } = useOrganization();
    const [copySuccess, setCopySuccess] = useState(false);
    const [pendingChangesCount, setPendingChangesCount] = useState(0);
    const [hasEntityPendingChanges, setHasEntityPendingChanges] = useState(false);
    const [loadingMemberData, setLoadingMemberData] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const copySignatureToClipboard = async () => {
        try {
            const html = generateHTML(grid, containerWidth, { subscriptionPlan });
            const htmlBlob = new Blob([html], { type: 'text/html' });
            const textContent = html.replace(/<[^>]*>/g, '');
            const textBlob = new Blob([textContent], { type: 'text/plain' });

            const clipboardItem = new ClipboardItem({
                'text/html': htmlBlob,
                'text/plain': textBlob
            });

            await navigator.clipboard.write([clipboardItem]);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Erreur lors de la copie:', err);
            alert('Erreur lors de la copie dans le presse-papier.');
        }
    };

    // Fonction pour rafraîchir les compteurs
    const refreshScheduledChanges = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    // Exposer la fonction de rafraîchissement au parent
    useEffect(() => {
        if (onRefreshNeeded) {
            onRefreshNeeded(refreshScheduledChanges);
        }
    }, [onRefreshNeeded]);

    // Charger le nombre de modifications en attente (global)
    useEffect(() => {
        const loadPendingChangesCount = async () => {
            if (!apiEnabled || apiStatus !== 'connected') {
                setPendingChangesCount(0);
                return;
            }

            try {
                const response = await API.getScheduledChanges({ status: 'pending' });
                const changesData = response.scheduled_changes || response.data?.scheduled_changes || response.data || [];
                const changes = Array.isArray(changesData) ? changesData : [];
                setPendingChangesCount(changes.length);
            } catch (err) {
                console.error('Erreur lors du chargement des modifications en attente:', err);
                setPendingChangesCount(0);
            }
        };

        loadPendingChangesCount();
    }, [apiEnabled, apiStatus, refreshTrigger]);

    // Vérifier si l'entité actuelle a des modifications en attente
    useEffect(() => {
        const checkEntityPendingChanges = async () => {
            if (!apiEnabled || apiStatus !== 'connected' || !hasEntity || !entityId) {
                setHasEntityPendingChanges(false);
                return;
            }

            try {
                const entityType = isTemplateMode ? 'template' : 'signature';
                const response = await API.getScheduledChanges({
                    status: 'pending',
                    entity_type: entityType,
                    entity_id: entityId
                });
                const changesData = response.scheduled_changes || response.data?.scheduled_changes || response.data || [];
                const changes = Array.isArray(changesData) ? changesData : [];
                setHasEntityPendingChanges(changes.length > 0);
            } catch (err) {
                console.error('Erreur lors de la vérification des modifications de l\'entité:', err);
                setHasEntityPendingChanges(false);
            }
        };

        checkEntityPendingChanges();
    }, [apiEnabled, apiStatus, hasEntity, entityId, isTemplateMode, refreshTrigger]);

    return (
        <>
            <div className="dashboard-toolbar">
                <div className="toolbar-left">
                    <h2
                        className="toolbar-title editable"
                        onClick={onRename}
                        title="Cliquer pour renommer"
                    >
                        {signatureName || (isTemplateMode ? 'Nouveau modèle' : 'Nouvelle signature')}
                        {hasEntityPendingChanges && (
                            <span className="pending-indicator" title="Modifications planifiées en attente">
                                ⏰
                            </span>
                        )}
                        <svg
                            className="edit-icon"
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                        </svg>
                    </h2>
                </div>

                <div className="toolbar-actions">
                    {/* Preview Toggle */}
                    <div className="preview-section">
                        <button
                            className={`btn btn-preview ${previewMode ? 'active' : ''}`}
                            onClick={() => setPreviewMode(!previewMode)}
                        >
                            👁️ Prévisualiser
                        </button>
                        {previewMode && (
                            <>
                                <select
                                    className="preview-member-select"
                                    value={previewMember?.id || ''}
                                    disabled={loadingMemberData}
                                    onChange={async (e) => {
                                        const memberId = e.target.value;
                                        if (memberId === '') {
                                            setPreviewMember(null);
                                        } else {
                                            setLoadingMemberData(true);
                                            try {
                                                const response = await API.getDirectoryUserLiveData(memberId);
                                                if (response?.data?.user) {
                                                    setPreviewMember({ id: memberId, ...response.data.user });
                                                } else {
                                                    const member = directoryMembers.find(m => String(m.id) === memberId);
                                                    setPreviewMember(member || null);
                                                }
                                            } catch (error) {
                                                console.error('Failed to fetch live user data:', error);
                                                const member = directoryMembers.find(m => String(m.id) === memberId);
                                                setPreviewMember(member || null);
                                            } finally {
                                                setLoadingMemberData(false);
                                            }
                                        }
                                    }}
                                >
                                    <option value="">Jean Dupont (démo)</option>
                                    {directoryMembers.map(member => (
                                        <option key={member.id} value={member.id}>
                                            {member.first_name} {member.last_name}
                                        </option>
                                    ))}
                                </select>
                                {loadingMemberData && (
                                    <span className="loading-indicator">Chargement...</span>
                                )}
                                {/* Copy Button - Only in preview mode */}
                                {!isTemplateMode && (
                                    <button
                                        className={`btn btn-copy ${copySuccess ? 'success' : ''}`}
                                        onClick={copySignatureToClipboard}
                                        disabled={grid.length === 0}
                                    >
                                        {copySuccess ? '✅ Copié !' : '📋 Copier'}
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Save with dropdown */}
                    {apiEnabled && apiStatus === 'connected' && (
                        <SaveDropdown
                            onSave={onSave}
                            onSchedule={hasEntity && onSchedule ? onSchedule : null}
                        />
                    )}

                    {/* View Versions */}
                    {apiEnabled && apiStatus === 'connected' && onViewScheduledChanges && (
                        <button
                            className="btn btn-info scheduled-changes-btn"
                            onClick={onViewScheduledChanges}
                            title="Voir les versions et modifications planifiées"
                        >
                            📚 Versions
                            {pendingChangesCount > 0 && (
                                <span className="pending-badge">{pendingChangesCount}</span>
                            )}
                        </button>
                    )}

                    {/* Clear */}
                    <button className="btn btn-danger" onClick={onClear}>
                        🗑️ Effacer
                    </button>
                </div>
            </div>
        </>
    );
}

export default DashboardToolbar;
